import React from 'react';
import { Box, FormHelperText, InputLabel, useTheme } from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

/**
 * E.164-friendly phone (country code + national number). Value stored like +14155552671.
 */
const InternationalPhoneField = ({
  label,
  name,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
  compact,
}) => {
  const theme = useTheme();
  const borderColor = error ? theme.palette.error.main : theme.palette.divider;
  const inputPad = compact ? '8px 10px' : '16.5px 14px';
  const fontSize = compact ? '0.875rem' : '1rem';

  return (
    <Box>
      {label && (
        <InputLabel
          shrink
          sx={{ position: 'static', transform: 'none', mb: 0.5, fontSize: '0.875rem' }}
          required={required}
        >
          {label}
        </InputLabel>
      )}
      <Box
        sx={{
          width: '100%',
          '& .PhoneInput': { display: 'flex', alignItems: 'center', width: '100%', gap: 1 },
          '& .PhoneInputCountry': { flexShrink: 0 },
          '& .PhoneInputCountrySelect': {
            padding: compact ? '4px 8px' : '8px 12px',
            minWidth: compact ? '4.5rem' : '5.75rem',
            fontSize: compact ? '0.8125rem' : '0.9375rem',
            fontWeight: 500,
          },
          '& .PhoneInputInput': {
            flex: 1,
            minWidth: 0,
            width: '100%',
            fontFamily: theme.typography.fontFamily,
            fontSize,
            padding: inputPad,
            borderRadius: `${theme.shape.borderRadius}px`,
            border: `1px solid ${borderColor}`,
            backgroundColor: disabled ? theme.palette.action.hover : theme.palette.background.paper,
            color: theme.palette.text.primary,
            outline: 'none',
            boxSizing: 'border-box',
            '&:focus': {
              borderWidth: 2,
              padding: compact ? '7px 9px' : '15.5px 13px',
              borderColor: theme.palette.primary.main,
            },
            '&:disabled': { opacity: 0.6 },
          },
        }}
      >
        <PhoneInput
          international
          defaultCountry="US"
          countryCallingCodeEditable={false}
          labels={{ country: 'Country code', phone: 'National number' }}
          value={value || undefined}
          onChange={(phone) => onChange({ target: { name, value: phone || '' } })}
          disabled={disabled}
          limitMaxLength
        />
      </Box>
      {(() => {
        const errMsg = typeof error === 'string' ? error : '';
        const defaultHint =
          !compact && !helperText && !errMsg
            ? 'Dialing code: use the country dropdown (flag / ISO) on the left, then enter the number.'
            : '';
        const msg = helperText || errMsg || defaultHint;
        return msg ? (
          <FormHelperText error={!!error} sx={{ mt: 0.5 }}>
            {msg}
          </FormHelperText>
        ) : null;
      })()}
    </Box>
  );
};

export default InternationalPhoneField;
